import moment from "moment";

import TypesUtils from "../../utils/typesUtils";
import MetConstants from "./metConstants";
import { TipoRepeticion } from "./agregacion/tiposAgregacion";

const DEFAULT_STR_SIN_DATO = "-";

class DatosVarsUI {
    constructor() {
        this.strSinDato = DEFAULT_STR_SIN_DATO;
        this.dirVientoComoPtosCardinales = true;
        this.usarDigitosAImprimir = true;
        this.calcUnidad = {};
        this.idVarCantDecimales = {};
    }

    /**
     * Habilita o deshabilita la opcion de considerar los digitos a imprimir de cada variable.
     * Si esta habilitado efectuara el redondeo a cada valor con el dato de digitos a imprimir de cada variable.
     *
     * @param {boolean} b
     */
    setUsarDigitosAImprimir(b) {
        this.usarDigitosAImprimir = b;
        return this;
    }

    /**
     * Setea el valor resultante cuando no hay dato disponible
     *
     * @param {String} strSinDato
     */
    setStrSinDato(strSinDato) {
        this.strSinDato = strSinDato;
        return this;
    }

    /**
     * Setea el valor resultante cuando no hay dato a vacio
     */
    setStrSinDatoStrVacio() {
        this.strSinDato = "";
        return this;
    }

    setDirVientoComoPtosCardinales(b) {
        this.dirVientoComoPtosCardinales = b;
        return this;
    }

    getValDatoNulo() {
        return this.strSinDato;
    }

    setCantDecimalesVar(idVar, cantDecimales) {
        this.idVarCantDecimales[idVar] = cantDecimales;
    }

    setCantDecimalesVars(idsVar, cantDecimales) {
        idsVar.forEach((idVar) => {
            this.idVarCantDecimales[idVar] = cantDecimales;
        });
    }

    /**
     * Retorna el dato listo para presentar aplicando los parametros definidos actualmente
     *
     * @param {import('./variable').default} variable
     * @param {String|number|null} dato
     */
    getDatoPresentacion(variable, dato) {
        if (TypesUtils.undefOrNull(dato)) {
            return this.strSinDato;
        }
        if (
            this.dirVientoComoPtosCardinales &&
            MetConstants.idsDirViento.includes(variable.id)
        ) {
            // Si es una direccion de viento y se marco la opcion de presentarlo como punto cardinal
            return this.getDirVientoPtosCardinalesStr(dato);
        }

        if (TypesUtils.isNumber(dato)) {
            if (variable.entera) {
                return dato;
            } else return this.getStrDeFloat(variable, dato);
        } else {
            // Si no es un numero, puede ser que sea una variable ValorYCodigo, por lo tanto los valores numericos vienen
            // en formato string

            // Primero verifico que sea un entero
            if (TypesUtils.isIntOrStrInt(dato)) return parseInt(dato);
            // Verifico que sea un valor real, si lo es debo evalar si aplicar o no redondeos
            let datoFloat = parseFloat(dato);
            if (TypesUtils.isNumber(datoFloat))
                return this.getStrDeFloat(variable, datoFloat);
            return dato;
        }
    }

    getStrDeFloat(variable, dato) {
        let fnUnidad = this.calcUnidad[variable.id];
        if (this.usarDigitosAImprimir && variable.nDigitosAlImprimir > 0) {
            let cantDecimales = variable.nDigitosAlImprimir;
            if (this.idVarCantDecimales[variable.id] !== undefined)
                cantDecimales = this.idVarCantDecimales[variable.id];
            return fnUnidad
                ? parseFloat(fnUnidad(dato).toFixed(cantDecimales))
                : parseFloat(dato.toFixed(cantDecimales));
        } else if (this.idVarCantDecimales[variable.id] !== undefined) {
            return fnUnidad
                ? parseFloat(
                      fnUnidad(dato).toFixed(
                          this.idVarCantDecimales[variable.id]
                      )
                  )
                : parseFloat(
                      dato.toFixed(this.idVarCantDecimales[variable.id])
                  );
        }
        return dato;
    }

    getDirVientoPtosCardinalesStr(dirViento) {
        if (!TypesUtils.isNumber(dirViento)) {
            if (dirViento === "variable" || dirViento === "calmo")
                return dirViento;
            dirViento = parseFloat(dirViento);
        }
        let dirVientoCorr;
        if (dirViento >= 348.75) {
            dirVientoCorr = dirViento + 11.25 - 360;
        } else {
            dirVientoCorr = dirViento + 11.25;
        }
        let strViento;
        if (dirVientoCorr <= 22.5) {
            strViento = "N";
        } else if (dirVientoCorr <= 45) {
            strViento = "NNE";
        } else if (dirVientoCorr <= 67.5) {
            strViento = "NE";
        } else if (dirVientoCorr <= 90) {
            strViento = "ENE";
        } else if (dirVientoCorr <= 112.5) {
            strViento = "E";
        } else if (dirVientoCorr <= 135) {
            strViento = "ESE";
        } else if (dirVientoCorr <= 157.5) {
            strViento = "SE";
        } else if (dirVientoCorr <= 180) {
            strViento = "SSE";
        } else if (dirVientoCorr <= 202.5) {
            strViento = "S";
        } else if (dirVientoCorr <= 225) {
            strViento = "SSW";
        } else if (dirVientoCorr <= 247.5) {
            strViento = "SW";
        } else if (dirVientoCorr <= 270) {
            strViento = "WSW";
        } else if (dirVientoCorr <= 292.5) {
            strViento = "W";
        } else if (dirVientoCorr <= 315) {
            strViento = "WNW";
        } else if (dirVientoCorr <= 337.5) {
            strViento = "NW";
        } else if (dirVientoCorr <= 360) {
            strViento = "NNW";
        }
        return strViento;
    }

    getFechaStrSegunRepeticion(fecha, tipoRepeticion) {
        moment.locale("es");
        let strFecha = "";
        if (tipoRepeticion === TipoRepeticion.noRepetir || !tipoRepeticion)
            strFecha = fecha;
        else {
            moment.locale("es");
            let momentDate = moment(fecha);
            if (tipoRepeticion === TipoRepeticion.diaDelAnio)
                strFecha = momentDate.dayOfYear();
            else if (tipoRepeticion === TipoRepeticion.diaDelMes)
                strFecha = momentDate.date();
            else if (tipoRepeticion === TipoRepeticion.horaDelDia)
                strFecha = momentDate.format("HH:00");
            else if (tipoRepeticion === TipoRepeticion.semanaDelAnio)
                strFecha = momentDate.week();
            else if (tipoRepeticion === TipoRepeticion.mesDelAnio)
                strFecha = momentDate.format("MMMM");
        }
        return strFecha;
    }

    setCalcUnidad(idVar, fnUnidad) {
        this.calcUnidad[idVar] = fnUnidad;
    }
}

export { DatosVarsUI as default };
